import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { retentionFor } from '@content-engine/shared';
import {
  archiveEntity,
  fetchBanners,
  fetchClip,
  regenerateClipApi,
  unarchiveEntity,
} from '../../lib/queries.ts';
import type { ClipDetailVariant } from '../../lib/types.ts';
import {
  ArchiveDialog,
  ArchivedChip,
  Capability,
  CapabilityBlock,
  FileChip,
  MediaPlaceholder,
  ParentLink,
  formatBytes,
  isAvailable,
  isFreed,
  shortDate,
} from '../../components/archive/index.ts';
import { BannerModal } from '../films/BannerModal.tsx';

function hms(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return [h, m, r].map((n) => String(n).padStart(2, '0')).join(':');
}

/**
 * Карточка клипа (прототип §3). Способности выводятся из состояния ФАЙЛОВ
 * (своего foundation и исходника фильма), а не из архивности родителя:
 * фильм в архиве с целым исходником клип перегенерировать не мешает.
 */
export function ClipDetailPage(): React.ReactElement {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showBannerModal, setShowBannerModal] = useState(false);

  const clipQuery = useQuery({
    queryKey: ['clip', id],
    queryFn: () => fetchClip(id),
    enabled: id !== '',
    refetchInterval: (q) => {
      const d = q.state.data;
      if (!d) return false;
      const pending = (s: string): boolean => s === 'pending' || s === 'rendering';
      return pending(d.clip.status) || d.variants.some((v) => pending(v.status)) ? 3_000 : false;
    },
  });

  const bannersQuery = useQuery({
    queryKey: ['banners', 'all'],
    queryFn: () => fetchBanners({ status: 'all' }),
    staleTime: 60_000,
  });
  const bannerNameById = new Map(
    (bannersQuery.data?.banners ?? []).map((b) => [b.id, b.name]),
  );

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['clip', id] });
    void queryClient.invalidateQueries({ queryKey: ['clips'] });
  };

  const archiveMutation = useMutation({
    mutationFn: (opts: { freeFiles: boolean }) => archiveEntity('clip', id, opts),
    onSuccess: () => {
      setShowArchiveDialog(false);
      invalidate();
    },
    onError: (error: Error) => {
      setShowArchiveDialog(false);
      alert(error.message);
    },
  });

  const unarchiveMutation = useMutation({
    mutationFn: () => unarchiveEntity('clip', id),
    onSuccess: invalidate,
    onError: (error: Error) => alert(error.message),
  });

  // Перерезка клипа из исходника фильма — ровно та способность, которую обещает
  // блок ниже. Ручка существующая: POST /clips/:id/regenerate.
  const regenerateMutation = useMutation({
    mutationFn: () => regenerateClipApi(id),
    onSuccess: invalidate,
    onError: (error: Error) => alert(error.message),
  });

  if (clipQuery.isLoading) return <div style={{ padding: 16 }}>{t('common.loading')}</div>;

  const data = clipQuery.data;
  if (!data) return <div style={{ padding: 16 }}>{t('clips.notFound')}</div>;

  const { clip, film, moment, file, variants, presignedUrl } = data;
  const title = moment?.title ?? t('clips.untitled');

  // Способность №1 — из foundation-файла самого клипа.
  const canVariant = isAvailable(file);
  // Способность №2 — из исходника ФИЛЬМА. Архивность фильма её не отнимает.
  const filmFile = film?.file ?? null;
  const canRegenClip = isAvailable(filmFile);
  const regenerateWhy = canRegenClip
    ? film?.archivedAt
      ? t('archive.capability.clip.regenerateOkArchivedFilm', {
          date: shortDate(filmFile?.freeAfter ?? null),
        })
      : t('archive.capability.clip.regenerateOk')
    : isFreed(filmFile)
      ? t('archive.capability.clip.regenerateNo')
      : t('archive.capability.clip.regenerateAbsent');

  return (
    <>
      <div className="page-head">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <h1>{title}</h1>
          <ArchivedChip archivedAt={clip.archivedAt} />
        </div>
        {clip.archivedAt && (
          <div className="filters">
            <button
              type="button"
              onClick={() => unarchiveMutation.mutate()}
              disabled={unarchiveMutation.isPending}
            >
              {t('clips.detail.unarchive')}
            </button>
          </div>
        )}
      </div>

      <div style={{ color: 'var(--text-3)', fontSize: 13, margin: '0 2px 14px' }}>
        <Link to="/clips" style={{ color: 'var(--neon)' }}>
          {t('clips.title')}
        </Link>{' '}
        / {title}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 18 }}>
        <div>
          {isFreed(file) ? (
            <MediaPlaceholder file={file} />
          ) : presignedUrl ? (
            <video
              src={presignedUrl}
              controls
              style={{
                width: '100%',
                maxHeight: 420,
                aspectRatio: '9 / 16',
                background: '#000',
                borderRadius: 10,
              }}
            />
          ) : (
            <div className="player">{t(`clips.status_${clip.status}`)}</div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
            <button
              type="button"
              onClick={() => setShowBannerModal(true)}
              disabled={!canVariant}
              title={canVariant ? undefined : t('archive.capability.clip.makeVariantNo', { date: shortDate(file.freedAt) })}
            >
              {t('clips.detail.createVariant')}
            </button>
            {/* Кнопка ровно там, где блок способностей обещает «перегенерировать
                сам клип», и гаснет по тому же условию — исходник фильма 'freed'. */}
            <button
              type="button"
              onClick={() => regenerateMutation.mutate()}
              disabled={!canRegenClip || regenerateMutation.isPending}
              title={canRegenClip ? undefined : regenerateWhy}
            >
              {regenerateMutation.isPending ? '…' : t('clips.detail.regenerate')}
            </button>
            <button type="button" onClick={() => setShowArchiveDialog(true)}>
              {t('clips.detail.archive')}
            </button>
          </div>
        </div>

        <div>
          <div className="section">
            <div className="section-head">
              <h2>{t('clips.detail.capabilities')}</h2>
            </div>
            <div className="section-body caps">
              <CapabilityBlock>
                <Capability
                  available={canVariant}
                  title={t('archive.capability.clip.makeVariant')}
                  why={
                    canVariant
                      ? t('archive.capability.clip.makeVariantOk')
                      : isFreed(file)
                        ? t('archive.capability.clip.makeVariantNo', { date: shortDate(file.freedAt) })
                        : t('archive.capability.clip.makeVariantAbsent')
                  }
                />
                <Capability
                  available={canRegenClip}
                  title={t('archive.capability.clip.regenerate')}
                  why={regenerateWhy}
                />
                <Capability
                  available
                  title={t('archive.capability.clip.watchVariants')}
                  why={t('archive.capability.clip.watchVariantsWhy', { count: variants.length })}
                />
              </CapabilityBlock>
            </div>
          </div>

          <div className="section">
            <div className="section-head">
              <h2>{t('clips.detail.params')}</h2>
            </div>
            <div className="section-body">
              <table className="kv">
                <tbody>
                  <tr>
                    <td>{t('clips.detail.moment')}</td>
                    <td>
                      {moment
                        ? `${hms(Number(moment.startSec))} – ${hms(Number(moment.endSec))}`
                        : '—'}
                    </td>
                  </tr>
                  <tr>
                    <td>{t('clips.detail.duration')}</td>
                    <td>
                      {clip.durationSec
                        ? t('clips.table.durationSec', { sec: Math.round(Number(clip.durationSec)) })
                        : '—'}
                    </td>
                  </tr>
                  <tr>
                    <td>{t('clips.detail.film')}</td>
                    <td>{film ? <ParentLink parent={film} kind="film" /> : '—'}</td>
                  </tr>
                  <tr>
                    <td>{t('clips.detail.foundationFile')}</td>
                    <td>
                      <FileChip file={file} />
                      {file.status === 'absent' && (
                        <span style={{ color: 'var(--text-3)' }}>
                          {t(`clips.status_${clip.status}`)}
                        </span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>{t('clips.detail.subtitles')}</td>
                    <td>
                      {clip.subtitleLanguage} · {clip.subtitleStyle}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="section">
            <div className="section-head">
              <h2>
                {t('clips.variantsLabel')}{' '}
                <span className="count">{variants.length}</span>
              </h2>
            </div>
            {variants.length === 0 ? (
              <div style={{ padding: 16, color: 'var(--text-3)' }}>{t('clips.detail.noVariants')}</div>
            ) : (
              <table>
                <tbody>
                  {variants.map((v) => (
                    <VariantRow
                      key={v.id}
                      variant={v}
                      bannerName={
                        (v.bannerLibraryId ? bannerNameById.get(v.bannerLibraryId) : null) ?? '—'
                      }
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {showArchiveDialog && (
        <ArchiveDialog
          kind="clip"
          freeAfterDays={retentionFor('clip_foundation')}
          sizeBytes={file.sizeBytes}
          variantsCount={variants.filter((v) => !v.archivedAt).length}
          busy={archiveMutation.isPending}
          onCancel={() => setShowArchiveDialog(false)}
          onConfirm={(opts) => archiveMutation.mutate(opts)}
        />
      )}

      {showBannerModal && (
        <BannerModal
          clipId={clip.id}
          onClose={() => setShowBannerModal(false)}
          onApplied={() => {
            setShowBannerModal(false);
            invalidate();
          }}
        />
      )}
    </>
  );
}

/** Строка варианта: архивный приглушён (.arch), но остаётся живым и открываемым. */
function VariantRow({
  variant,
  bannerName,
}: {
  variant: ClipDetailVariant;
  bannerName: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const size = formatBytes(variant.sizeBytes);
  return (
    <tr className={variant.archivedAt ? 'arch' : undefined}>
      <td>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {isFreed(variant.file) ? (
            <MediaPlaceholder file={variant.file} variant="thumb" />
          ) : (
            <div className="thumb" />
          )}
          <div>
            <div style={{ fontWeight: 600 }}>
              {t(`clips.variantStatus_${variant.status}`)} · {variant.position} {variant.sizePct}%
            </div>
            <div style={{ color: 'var(--text-3)' }}>
              {bannerName}
              {size ? ` · ${size}` : ''}
            </div>
          </div>
        </div>
      </td>
      <td>
        <FileChip file={variant.file} /> <ArchivedChip archivedAt={variant.archivedAt} />
      </td>
      <td>
        <Link to={`/variants/${variant.id}`}>
          <button type="button">{t('common.open')}</button>
        </Link>
      </td>
    </tr>
  );
}
